import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Table,
    Input,
    FormGroup,
    Label,
    Row,
    Col,
    Card,
    CardBody,
    Alert,
    Badge
} from 'reactstrap';
import { 
    FaPlus, 
    FaTrash, 
    FaCalculator, 
    FaFileInvoice, 
    FaExclamationTriangle,
    FaCheckCircle,
    FaEdit
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

const SubInvoiceManager = ({ 
    isOpen, 
    toggle, 
    parentInvoice, 
    onCreateSubInvoices,
    existingSubInvoices = []
}) => {
    const { t } = useTranslation();
    const [subInvoices, setSubInvoices] = useState([]);
    const [totalAllocated, setTotalAllocated] = useState(0);
    const [remainingAmount, setRemainingAmount] = useState(0);

    // Initialiser les sous-factures
    useEffect(() => {
        if (parentInvoice && isOpen) {
            const existingTotal = existingSubInvoices.reduce((sum, sub) => sum + sub.montantTTC, 0);
            const remaining = parentInvoice.montantTotalTTC - existingTotal;
            
            if (existingSubInvoices.length > 0) {
                setSubInvoices(existingSubInvoices.map(sub => ({
                    id: sub.id,
                    numero: sub.numero,
                    montant: sub.montantTTC,
                    description: sub.description || '',
                    isExisting: true
                })));
                setTotalAllocated(existingTotal);
                setRemainingAmount(remaining);
            } else {
                // Créer une première sous-facture par défaut
                setSubInvoices([{
                    id: null,
                    numero: generateSubInvoiceNumber(parentInvoice.numero, 1),
                    montant: remaining,
                    description: '',
                    isExisting: false
                }]);
                setTotalAllocated(remaining);
                setRemainingAmount(0);
            }
        }
    }, [parentInvoice, isOpen, existingSubInvoices]);

    // Générer le numéro de sous-facture
    const generateSubInvoiceNumber = (parentNumber, index) => {
        return `${parentNumber}-${String(index).padStart(2, '0')}`;
    };

    // Ajouter une nouvelle sous-facture
    const addSubInvoice = () => {
        if (remainingAmount <= 0) {
            Swal.fire({
                icon: 'warning',
                title: t('subInvoice.noRemainingAmount'),
                text: t('subInvoice.noRemainingAmountMessage')
            });
            return;
        }

        const newIndex = subInvoices.filter(sub => !sub.isExisting).length + 1;
        const newSubInvoice = {
            id: null,
            numero: generateSubInvoiceNumber(parentInvoice.numero, existingSubInvoices.length + newIndex),
            montant: remainingAmount,
            description: '',
            isExisting: false
        };

        setSubInvoices([...subInvoices, newSubInvoice]);
        setTotalAllocated(totalAllocated + remainingAmount);
        setRemainingAmount(0);
    };

    // Supprimer une sous-facture
    const removeSubInvoice = (index) => {
        const subInvoice = subInvoices[index];
        
        if (subInvoice.isExisting) {
            Swal.fire({
                icon: 'warning',
                title: t('subInvoice.cannotDeleteExisting'),
                text: t('subInvoice.cannotDeleteExistingMessage')
            });
            return;
        }

        const newSubInvoices = subInvoices.filter((_, i) => i !== index);
        setSubInvoices(newSubInvoices);
        
        // Recalculer les totaux
        const newTotal = newSubInvoices.reduce((sum, sub) => sum + (sub.montant || 0), 0);
        setTotalAllocated(newTotal);
        setRemainingAmount(parentInvoice.montantTotalTTC - newTotal);
    };

    // Mettre à jour le montant d'une sous-facture
    const updateSubInvoiceAmount = (index, newAmount) => {
        const amount = parseFloat(newAmount) || 0;
        const updatedSubInvoices = [...subInvoices];
        updatedSubInvoices[index].montant = amount;
        
        setSubInvoices(updatedSubInvoices);
        
        // Recalculer les totaux
        const newTotal = updatedSubInvoices.reduce((sum, sub) => sum + (sub.montant || 0), 0);
        setTotalAllocated(newTotal);
        setRemainingAmount(parentInvoice.montantTotalTTC - newTotal);
    };

    // Mettre à jour la description d'une sous-facture
    const updateSubInvoiceDescription = (index, description) => {
        const updatedSubInvoices = [...subInvoices];
        updatedSubInvoices[index].description = description;
        setSubInvoices(updatedSubInvoices);
    };

    // Répartir équitablement
    const distributeEvenly = () => {
        const nonExistingSubInvoices = subInvoices.filter(sub => !sub.isExisting);
        if (nonExistingSubInvoices.length === 0) return;

        const amountPerSubInvoice = remainingAmount / nonExistingSubInvoices.length;
        
        const updatedSubInvoices = subInvoices.map(sub => {
            if (!sub.isExisting) {
                return { ...sub, montant: parseFloat(amountPerSubInvoice.toFixed(2)) };
            }
            return sub;
        });

        setSubInvoices(updatedSubInvoices);
        
        const newTotal = updatedSubInvoices.reduce((sum, sub) => sum + (sub.montant || 0), 0);
        setTotalAllocated(newTotal);
        setRemainingAmount(parentInvoice.montantTotalTTC - newTotal);
    };

    // Valider et créer les sous-factures
    const handleCreateSubInvoices = () => {
        // Validations
        if (Math.abs(remainingAmount) > 0.01) {
            Swal.fire({
                icon: 'error',
                title: t('subInvoice.invalidTotal'),
                text: t('subInvoice.invalidTotalMessage')
            });
            return;
        }

        const newSubInvoices = subInvoices.filter(sub => !sub.isExisting);
        if (newSubInvoices.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('subInvoice.noNewSubInvoices'),
                text: t('subInvoice.noNewSubInvoicesMessage')
            });
            return;
        }

        // Vérifier que tous les montants sont valides
        const invalidSubInvoices = newSubInvoices.filter(sub => !sub.montant || sub.montant <= 0);
        if (invalidSubInvoices.length > 0) {
            Swal.fire({
                icon: 'error',
                title: t('subInvoice.invalidAmounts'),
                text: t('subInvoice.invalidAmountsMessage')
            });
            return;
        }

        // Confirmer la création
        Swal.fire({
            title: t('subInvoice.confirmCreation'),
            text: t('subInvoice.confirmCreationMessage', { count: newSubInvoices.length }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                onCreateSubInvoices(newSubInvoices);
                toggle();
            }
        });
    };

    // Formater le prix
    const formatPrice = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 6
        }).format(Number(amount) || 0) + ' DH';
    };

    if (!parentInvoice) return null;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>
                <FaFileInvoice className="me-2" />
                {t('subInvoice.title')} - {parentInvoice.numero}
            </ModalHeader>
            <ModalBody>
                {/* Informations de la facture parent */}
                <Card className="mb-4">
                    <CardBody>
                        <Row>
                            <Col md={6}>
                                <h6 className="text-muted">{t('subInvoice.parentInvoice')}</h6>
                                <div><strong>{parentInvoice.numero}</strong></div>
                                <div>{t('facture.client')}: {parentInvoice.clientNom}</div>
                                <div>{t('facture.date')}: {new Date(parentInvoice.dateCreation).toLocaleDateString('fr-FR')}</div>
                            </Col>
                            <Col md={6}>
                                <div className="text-end">
                                    <div className="mb-2">
                                        <span className="text-muted">{t('facture.totalAmount')}: </span>
                                        <strong className="fs-5">{formatPrice(parentInvoice.montantTotalTTC)}</strong>
                                    </div>
                                    <div className="mb-2">
                                        <span className="text-muted">{t('subInvoice.allocated')}: </span>
                                        <span className="text-info">{formatPrice(totalAllocated)}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted">{t('subInvoice.remaining')}: </span>
                                        <span className={`fw-bold ${remainingAmount === 0 ? 'text-success' : remainingAmount > 0 ? 'text-warning' : 'text-danger'}`}>
                                            {formatPrice(remainingAmount)}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Alerte de validation */}
                {Math.abs(remainingAmount) > 0.01 && (
                    <Alert color={remainingAmount > 0 ? "warning" : "danger"}>
                        <FaExclamationTriangle className="me-2" />
                        {remainingAmount > 0 
                            ? t('subInvoice.remainingAmountWarning', { amount: formatPrice(remainingAmount) })
                            : t('subInvoice.excessAmountWarning', { amount: formatPrice(Math.abs(remainingAmount)) })
                        }
                    </Alert>
                )}

                {/* Boutons d'action */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <Button color="primary" size="sm" onClick={addSubInvoice} className="me-2">
                            <FaPlus className="me-1" />
                            {t('subInvoice.addSubInvoice')}
                        </Button>
                        <Button color="info" size="sm" onClick={distributeEvenly}>
                            <FaCalculator className="me-1" />
                            {t('subInvoice.distributeEvenly')}
                        </Button>
                    </div>
                    <Badge color={Math.abs(remainingAmount) <= 0.01 ? "success" : "warning"}>
                        {Math.abs(remainingAmount) <= 0.01 ? (
                            <>
                                <FaCheckCircle className="me-1" />
                                {t('subInvoice.balanced')}
                            </>
                        ) : (
                            <>
                                <FaExclamationTriangle className="me-1" />
                                {t('subInvoice.unbalanced')}
                            </>
                        )}
                    </Badge>
                </div>

                {/* Tableau des sous-factures */}
                <Table bordered hover>
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: '25%' }}>{t('subInvoice.number')}</th>
                            <th style={{ width: '20%' }}>{t('subInvoice.amount')}</th>
                            <th style={{ width: '35%' }}>{t('subInvoice.description')}</th>
                            <th style={{ width: '10%' }}>{t('subInvoice.status')}</th>
                            <th style={{ width: '10%' }}>{t('common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subInvoices.map((subInvoice, index) => (
                            <tr key={index}>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <FaFileInvoice className="me-2 text-muted" />
                                        {subInvoice.numero}
                                        {subInvoice.isExisting && (
                                            <Badge color="success" className="ms-2" size="sm">
                                                {t('subInvoice.existing')}
                                            </Badge>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        onFocus={(e) => e.target.select()} // Select the field content on focus
                                        value={subInvoice.montant || ''}
                                        onChange={(e) => updateSubInvoiceAmount(index, e.target.value)}
                                        disabled={subInvoice.isExisting}
                                        step="0.01"
                                        min="0"
                                        className={subInvoice.isExisting ? 'bg-light' : ''}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        value={subInvoice.description || ''}
                                        onChange={(e) => updateSubInvoiceDescription(index, e.target.value)}
                                        disabled={subInvoice.isExisting}
                                        placeholder={t('subInvoice.descriptionPlaceholder')}
                                        className={subInvoice.isExisting ? 'bg-light' : ''}
                                    />
                                </td>
                                <td>
                                    {subInvoice.isExisting ? (
                                        <Badge color="success">
                                            <FaCheckCircle className="me-1" />
                                            {t('subInvoice.created')}
                                        </Badge>
                                    ) : (
                                        <Badge color="warning">
                                            <FaEdit className="me-1" />
                                            {t('subInvoice.draft')}
                                        </Badge>
                                    )}
                                </td>
                                <td>
                                    {!subInvoice.isExisting && (
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => removeSubInvoice(index)}
                                            title={t('subInvoice.delete')}
                                        >
                                            <FaTrash />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {subInvoices.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center py-4 text-muted">
                                    {t('subInvoice.noSubInvoices')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {/* Résumé des totaux */}
                <Card className="mt-3">
                    <CardBody className="bg-light">
                        <Row>
                            <Col md={4}>
                                <div className="text-center">
                                    <div className="text-muted small">{t('subInvoice.totalParent')}</div>
                                    <div className="h5 mb-0">{formatPrice(parentInvoice.montantTotalTTC)}</div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center">
                                    <div className="text-muted small">{t('subInvoice.totalAllocated')}</div>
                                    <div className="h5 mb-0 text-info">{formatPrice(totalAllocated)}</div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center">
                                    <div className="text-muted small">{t('subInvoice.remaining')}</div>
                                    <div className={`h5 mb-0 ${remainingAmount === 0 ? 'text-success' : remainingAmount > 0 ? 'text-warning' : 'text-danger'}`}>
                                        {formatPrice(remainingAmount)}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Instructions d'utilisation */}
                <Alert color="info" className="mt-3">
                    <h6 className="alert-heading">{t('subInvoice.instructions')}</h6>
                    <ul className="mb-0 small">
                        <li>{t('subInvoice.instruction1')}</li>
                        <li>{t('subInvoice.instruction2')}</li>
                        <li>{t('subInvoice.instruction3')}</li>
                        <li>{t('subInvoice.instruction4')}</li>
                    </ul>
                </Alert>
            </ModalBody>
            <ModalFooter>
                <div className="d-flex justify-content-between w-100">
                    <div className="d-flex align-items-center">
                        <span className="text-muted small">
                            {t('subInvoice.newSubInvoicesCount')}: {subInvoices.filter(sub => !sub.isExisting).length}
                        </span>
                    </div>
                    <div>
                        <Button 
                            color="success" 
                            onClick={handleCreateSubInvoices}
                            disabled={Math.abs(remainingAmount) > 0.01 || subInvoices.filter(sub => !sub.isExisting).length === 0}
                        >
                            <FaPlus className="me-1" />
                            {t('subInvoice.createSubInvoices')}
                        </Button>
                        <Button color="secondary" onClick={toggle} className="ms-2">
                            {t('common.cancel')}
                        </Button>
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default SubInvoiceManager;
